import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { getCurrentUser } from '../../utils/api';
import { getAuthToken } from '../../utils/cookies';
import { cn } from '../../utils/cn';

const AUTH_REDIRECT_STORAGE_KEY = 'prepxai_auth_redirect';

const Onboarding = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [direction, setDirection] = useState(1); // 1 for forward, -1 for back
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [resumeFile, setResumeFile] = useState(null);
    const [email, setEmail] = useState('');

    const [formData, setFormData] = useState({
        full_name: '',
        phone_number: '',
        college: '',
        graduation_year: '',
        target_role: '',
        is_private: false,
        resume_url: '',
        resume_data: '',
        referral_code: ''
    });

    // Total steps for progress bar
    const totalSteps = 10;

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                setLoading(true);
                const token = getAuthToken();
                const userData = await getCurrentUser(token);
                const profile = userData?.user || userData;
                if (profile) {
                    setEmail(profile.email || '');
                    setFormData(prev => ({
                        ...prev,
                        full_name: profile.full_name || '',
                        phone_number: profile.phone_number || '',
                        college: profile.college || '',
                        graduation_year: profile.graduation_year ? String(profile.graduation_year) : '',
                        target_role: profile.target_role || '',
                        is_private: profile.is_private || false,
                        resume_url: profile.resume_url || '',
                        resume_data: profile.resume_data || ''
                    }));
                }
            } catch (err) {
                console.error("Failed to fetch user profile", err);
                const userEmail = localStorage.getItem('user_email') || '';
                setEmail(userEmail);
            } finally {
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const nextStep = useCallback(() => {
        setDirection(1);
        setStep(s => Math.min(s + 1, totalSteps - 1));
    }, []);

    const prevStep = useCallback(() => {
        setDirection(-1);
        setStep(s => Math.max(s - 1, 0));
    }, []);

    const handleResumeUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formDataUpload = new FormData();
        formDataUpload.append('resume', file);

        try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/resume`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`
                },
                body: formDataUpload,
                credentials: 'include'
            });

            if (!response.ok) {
                const errorBody = await response.json().catch(() => ({}));
                throw new Error(errorBody.error || 'Failed to upload resume');
            }

            const result = await response.json();
            const data = result.resume_data || {};

            setFormData(prev => ({
                ...prev,
                full_name: data.name || prev.full_name,
                target_role: data.current_role || prev.target_role,
                resume_url: result.resume_url,
                resume_data: JSON.stringify(data)
            }));

            setResumeFile(file);
            nextStep();
        } catch (err) {
            console.error('Resume upload error:', err);
            alert(err.message || 'Failed to parse resume. You can still fill the details manually.');
        } finally {
            setUploading(false);
        }
    };

    const handleProfileSubmit = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(getAuthToken() ? { 'Authorization': `Bearer ${getAuthToken()}` } : {})
                },
                body: JSON.stringify({
                    ...formData,
                    graduation_year: parseInt(formData.graduation_year) || 0
                }),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorBody = await response.json().catch(() => ({}));
                throw new Error(errorBody.error || 'Failed to update profile');
            }

            // Success
        } catch (err) {
            console.error('Onboarding error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const finishOnboarding = async () => {
        setLoading(true);
        try {
            await handleProfileSubmit();

            localStorage.setItem('onboarded', 'true');

            setTimeout(() => {
                const redirectTo = sessionStorage.getItem(AUTH_REDIRECT_STORAGE_KEY) || '/interview-setup';
                sessionStorage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
                navigate(redirectTo);
            }, 500);
        } catch (err) {
            console.error("Failed to finish onboarding", err);
            navigate('/interview-setup');
        } finally {
            setLoading(false);
        }
    };

    // Keyboard listener for Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                if (e.target.tagName !== 'BUTTON' && step < totalSteps - 1) {
                    if (step === 1 && !formData.full_name) return;
                    if (step === 3 && !formData.college) return;
                    if (step === 4 && !formData.graduation_year) return;
                    if (step === 5 && !formData.target_role) return;

                    if (step !== 7 && step !== 9) {
                        nextStep();
                    }
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [step, formData, nextStep]);

    // Animation variants
    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 30 : -30,
            opacity: 0,
            scale: 0.98
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 30 : -30,
            opacity: 0,
            scale: 0.98
        })
    };

    const renderNavigation = () => {
        let canGoNext = true;
        if (step === 1) canGoNext = !!formData.full_name;
        else if (step === 3) canGoNext = !!formData.college;
        else if (step === 4) canGoNext = !!formData.graduation_year;
        else if (step === 5) canGoNext = !!formData.target_role;

        return (
            <div className="flex justify-center items-center gap-4 pt-4 pb-2">
                <button
                    type="button"
                    onClick={prevStep}
                    disabled={step === 0}
                    className="flex items-center justify-center bg-transparent text-muted-foreground hover:text-foreground transition-all duration-200 outline-none active:scale-95 disabled:pointer-events-none disabled:opacity-20"
                    aria-label="Previous step"
                >
                    <Icon name="ArrowLeft" size={20} />
                </button>
                <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext}
                    className="flex items-center justify-center bg-transparent text-muted-foreground hover:text-foreground transition-all duration-200 outline-none active:scale-95 disabled:pointer-events-none disabled:opacity-20"
                    aria-label="Next step"
                >
                    <Icon name="ArrowRight" size={20} />
                </button>
            </div>
        );
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <div className="flex flex-col items-center text-center space-y-5 py-6 w-full max-w-lg mx-auto">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 100, damping: 15 }}
                            className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shadow-[0_4px_12px_rgba(22,152,115,0.08)]"
                        >
                            <Icon name="Rocket" size={20} />
                        </motion.div>
                        <div className="space-y-1.5">
                            <h2 className="text-[16px] font-semibold tracking-tight text-foreground">
                                Let's build your success plan
                            </h2>
                            <p className="text-[12.5px] leading-relaxed text-muted-foreground max-w-[320px] mx-auto text-balance">
                                Answer a few quick questions so we can personalize your interview preparation.
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 w-full max-w-[220px] pt-2">
                            <Button
                                variant="default"
                                size="sm"
                                className="rounded-[10px] text-[13px] font-semibold h-[38px]"
                                onClick={nextStep}
                                fullWidth
                            >
                                Start
                            </Button>
                            {/* <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-[10px] text-[11.5px] font-semibold text-muted-foreground hover:!bg-transparent hover:text-foreground"
                                onClick={finishOnboarding}
                                fullWidth
                            >
                                I'll do this later
                            </Button> */}
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div className="space-y-5 w-full py-2 max-w-md mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 1 • Basics</span>
                            <h2 className="text-[16px] font-semibold text-foreground">What should we call you?</h2>
                            <p className="text-[12.5px] text-muted-foreground">This helps us personalize your interview feedback.</p>
                        </div>
                        <div className="space-y-3.5">
                            <input
                                type="text"
                                name="full_name"
                                value={formData.full_name}
                                onChange={handleChange}
                                placeholder="Enter your full name"
                                className="h-10 w-full rounded-[10px] border border-border bg-white dark:bg-[#08080a] px-3.5 text-[13px] font-medium text-foreground outline-none transition placeholder:text-muted-foreground/40 focus:border-primary focus:ring-1 focus:ring-primary/20 w-full"
                                autoFocus
                            />
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-5 w-full py-2 max-w-md mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 2 • Contact</span>
                            <h2 className="text-[16px] font-semibold text-foreground">Where can we send important updates?</h2>
                            <p className="text-[12.5px] text-muted-foreground">We never spam. Only important reminders.</p>
                        </div>
                        <div className="space-y-3.5">
                            <input
                                type="tel"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                placeholder="+1 234 567 8900"
                                className="h-10 w-full rounded-[10px] border border-border bg-white dark:bg-[#08080a] px-3.5 text-[13px] font-medium text-foreground outline-none transition placeholder:text-muted-foreground/40 focus:border-primary focus:ring-1 focus:ring-primary/20 w-full font-mono"
                                autoFocus
                            />
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-5 w-full py-2 max-w-md mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 3 • Academic</span>
                            <h2 className="text-[16px] font-semibold text-foreground">Which college are you from?</h2>
                            <p className="text-[12.5px] text-muted-foreground">This helps us compare prep trends across colleges.</p>
                        </div>
                        <div className="space-y-3.5">
                            <Select
                                options={[
                                    { value: 'IIT Kanpur', label: 'IIT Kanpur' },
                                    { value: 'IIT Bombay', label: 'IIT Bombay' },
                                    { value: 'IIT Delhi', label: 'IIT Delhi' },
                                    { value: 'IIT Roorkee', label: 'IIT Roorkee' },
                                    { value: 'IIT Hyderabad', label: 'IIT Hyderabad' },
                                    { value: 'NIT Trichy', label: 'NIT Trichy' },
                                    { value: 'BITS Pilani', label: 'BITS Pilani' },
                                    { value: 'Other', label: 'Other' }
                                ]}
                                value={formData.college}
                                onChange={(val) => {
                                    setFormData(p => ({ ...p, college: val }));
                                    setTimeout(nextStep, 350);
                                }}
                                searchable
                                placeholder="Search your college..."
                                className="text-[13px] rounded-[10px] onboarding-college-select"
                            />
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-5 w-full py-2 max-w-lg mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 4 • Timeline</span>
                            <h2 className="text-[16px] font-semibold text-foreground">When are you graduating?</h2>
                        </div>
                        <div className="space-y-3.5">
                            <div className="grid grid-cols-2 gap-2.5">
                                {['2026', '2027','2028', 'Already Graduated'].map((year) => (
                                    <button
                                        key={year}
                                        type="button"
                                        onClick={() => {
                                            setFormData(p => ({ ...p, graduation_year: year }));
                                            setTimeout(nextStep, 300);
                                        }}
                                        className={cn(
                                            "py-3 px-4 rounded-[10px] border text-center transition-all duration-200 outline-none text-[13px] font-medium",
                                            formData.graduation_year === year
                                                ? "border-primary bg-primary/5 text-primary"
                                                : "border-border bg-white dark:bg-[#111216] text-foreground hover:border-primary/30"
                                        )}
                                    >
                                        {year}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="space-y-5 w-full py-2 max-w-lg mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 5 • Ambition</span>
                            <h2 className="text-[16px] font-semibold text-foreground">What role are you preparing for?</h2>
                        </div>
                        <div className="space-y-3.5">
                            <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto custom-scrollbar-mini pr-1">
                                {[
                                    { id: 'SWE', label: 'Software Engineer', icon: 'Laptop' },
                                    { id: 'AIML', label: 'AI / ML Engineer', icon: 'Sparkles' },
                                    { id: 'DS', label: 'Data Scientist', icon: 'BarChart' },
                                    { id: 'BE', label: 'Backend Engineer', icon: 'Server' },
                                    { id: 'FE', label: 'Frontend Engineer', icon: 'Palette' },
                                    { id: 'Other', label: 'Other', icon: 'HelpCircle' }
                                ].map((role) => (
                                    <button
                                        key={role.id}
                                        type="button"
                                        onClick={() => {
                                            setFormData(p => ({ ...p, target_role: role.label }));
                                            setTimeout(nextStep, 300);
                                        }}
                                        className={cn(
                                            "p-2.5 rounded-[10px] border text-left transition-all duration-200 flex items-center gap-2.5 outline-none text-[12.5px] font-medium w-full",
                                            formData.target_role === role.label
                                                ? "border-primary bg-primary/5 text-primary"
                                                : "border-border bg-white dark:bg-[#111216] text-foreground hover:border-primary/30"
                                        )}
                                    >
                                        <div className={cn(
                                            "w-7.5 h-7.5 rounded-md flex items-center justify-center shrink-0",
                                            formData.target_role === role.label ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                                        )}>
                                            <Icon name={role.icon} size={14} />
                                        </div>
                                        <span className="truncate">{role.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="space-y-5 w-full py-2 max-w-lg mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 6 • Visibility</span>
                            <h2 className="text-[16px] font-semibold text-foreground">Do you want your profile to be public?</h2>
                        </div>
                        <div className="space-y-3.5">
                            <div className="flex flex-col gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData(p => ({ ...p, is_private: false }));
                                        setTimeout(nextStep, 300);
                                    }}
                                    className={cn(
                                        "p-3.5 rounded-[10px] border text-left transition-all duration-200 flex items-start gap-3.5 outline-none w-full",
                                        !formData.is_private
                                            ? "border-primary bg-primary/5 text-primary"
                                            : "border-border bg-white dark:bg-[#111216] text-foreground hover:border-primary/30"
                                    )}
                                >
                                    <div className={cn(
                                        "w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5",
                                        !formData.is_private ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                                    )}>
                                        <Icon name="Globe" size={15} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <h3 className="text-[13px] font-semibold">Public Profile</h3>
                                        <p className="text-[11.5px] leading-relaxed text-muted-foreground">Discoverable by recruiters and peers.</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData(p => ({ ...p, is_private: true }));
                                        setTimeout(nextStep, 300);
                                    }}
                                    className={cn(
                                        "p-3.5 rounded-[10px] border text-left transition-all duration-200 flex items-start gap-3.5 outline-none w-full",
                                        formData.is_private
                                            ? "border-primary bg-primary/5 text-primary"
                                            : "border-border bg-white dark:bg-[#111216] text-foreground hover:border-primary/30"
                                    )}
                                >
                                    <div className={cn(
                                        "w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5",
                                        formData.is_private ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                                    )}>
                                        <Icon name="Lock" size={15} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <h3 className="text-[13px] font-semibold">Private Profile</h3>
                                        <p className="text-[11.5px] leading-relaxed text-muted-foreground">Only visible to you. Anonymized data.</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 7:
                return (
                    <div className="space-y-5 w-full py-2 max-w-lg mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 7 • Resume</span>
                            <h2 className="text-[16px] font-semibold text-foreground">Supercharge with AI</h2>
                            <p className="text-[12.5px] text-muted-foreground text-balance">Upload your resume to get more personalized interview experience.</p>
                        </div>
                        <div className="space-y-3.5">
                            <div className={cn(
                                "relative border border-dashed rounded-[10px] p-6 transition-all flex flex-col items-center justify-center gap-3 text-center group min-h-[130px]",
                                uploading ? "bg-muted/30 border-muted animate-pulse" : "bg-white dark:bg-[#111216] border-border hover:border-primary/40 hover:bg-primary/5"
                            )}>
                                {uploading ? (
                                    <div className="space-y-2 py-1.5">
                                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                                        <p className="text-[11.5px] font-semibold text-foreground">Reading resume...</p>
                                    </div>
                                ) : resumeFile ? (
                                    <div className="space-y-2.5">
                                        <div className="w-9 h-9 bg-green-500/10 rounded-lg flex items-center justify-center mx-auto text-green-500">
                                            <Icon name="FileCheck" size={18} />
                                        </div>
                                        <div>
                                            <p className="text-[12.5px] font-bold text-foreground max-w-[200px] truncate mx-auto">{resumeFile.name}</p>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setResumeFile(null);
                                                }}
                                                className="text-[10.5px] text-destructive hover:underline mt-0.5 font-semibold"
                                            >
                                                Change file
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105 text-primary">
                                            <Icon name="UploadCloud" size={18} />
                                        </div>
                                        <div className="space-y-0.5">
                                            <p className="text-[12.5px] font-semibold text-foreground">Drop your resume here</p>
                                            <p className="text-[10px] text-muted-foreground">PDF format, max 5MB</p>
                                        </div>
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={handleResumeUpload}
                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );

            case 8:
                return (
                    <div className="space-y-5 w-full py-2 max-w-lg mx-auto">
                        <div className="space-y-1.5 text-center">
                            <span className="text-[9px] font-boldtracking-widest text-primary">Step 8 • Referral</span>
                            <h2 className="text-[16px] font-semibold text-foreground">Got a referral code?</h2>
                            <p className="text-[12.5px] text-muted-foreground">Enter it below to unlock bonus interview coins.</p>
                        </div>
                        <div className="space-y-3.5">
                            <input
                                type="text"
                                name="referral_code"
                                value={formData.referral_code}
                                onChange={handleChange}
                                placeholder="PRO-LEVEL-99"
                                className="h-10 w-full rounded-[10px] border border-border bg-white dark:bg-[#08080a] px-3.5 text-centerfont-mono tracking-widest text-[13px] font-medium text-foreground outline-none transition placeholder:text-muted-foreground/40 focus:border-primary focus:ring-1 focus:ring-primary/20 w-full"
                                autoFocus
                            />
                            <div className="flex flex-col gap-2.5">
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className="text-muted-foreground hover:text-foreground underline decoration-dashed underline-offset-4 text-[10.5px] font-semibold text-center mt-0.5 outline-none animate-fade-in"
                                >
                                    I don't have one
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 9:
                return (
                    <div className="space-y-5 w-full py-2">
                        <div className="space-y-1.5 text-center">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 100, damping: 15, delay: 0.1 }}
                                className="w-8 h-8 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto mb-1"
                            >
                                <Icon name="Check" size={16} />
                            </motion.div>
                            <h2 className="text-[16px] font-semibold text-foreground">You're ready to start!</h2>
                            <p className="text-[12.5px] text-muted-foreground">Your pilot account is ready.</p>
                        </div>

                        <div className="flex justify-center">
                            <Button variant="default" size="sm" className="h-9 min-w-[220px] px-6 text-[11.5px] font-semibold rounded-lg" onClick={finishOnboarding}>
                                Continue to Dashboard
                            </Button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-[#08080a] flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Custom styling overrides for the dropdown select inside onboarding */}
            <style>{`
                /* Limit options list height and enable scroll */
                .onboarding-college-select .overflow-auto {
                    max-height: 110px !important;
                    overflow-y: auto !important;
                }

                /* Make select trigger button smaller */
                .onboarding-college-select button[id^="select-"] {
                    height: 34px !important;
                    font-size: 12.5px !important;
                    padding-left: 12px !important;
                    padding-right: 12px !important;
                    border-radius: 8px !important;
                }

                /* Make dropdown search input smaller */
                .onboarding-college-select input[placeholder="Search options..."] {
                    height: 32px !important;
                    font-size: 12px !important;
                    border-radius: 8px !important;
                }

                /* Adjust search icon position in dropdown search input */
                .onboarding-college-select svg[class*="text-muted-foreground"] {
                    width: 13px !important;
                    height: 13px !important;
                    top: 10px !important;
                }

                /* Make dropdown option list items smaller */
                .onboarding-college-select div[class*="cursor-pointer"] {
                    padding-top: 5px !important;
                    padding-bottom: 5px !important;
                    font-size: 12px !important;
                }

                /* Override neon green hover background color to neutral */
                .onboarding-college-select div[class*="cursor-pointer"]:hover {
                    background-color: var(--color-muted) !important;
                    color: var(--color-foreground) !important;
                }

                /* Selected option highlight color (professional forest green) */
                .onboarding-college-select div[class*="bg-primary"] {
                    background-color: var(--color-primary) !important;
                    color: var(--color-primary-foreground) !important;
                }
            `}</style>

            {/* Blurred background layout to simulate a popup modal overlaying the actual platform */}
            <div className="absolute inset-0 z-0 flex flex-col select-none pointer-events-none filter blur-[3px] opacity-25">
                {/* Dummy Header */}
                <div className="h-16 border-b border-border bg-background px-6 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center text-white"><Icon name="Sparkles" size={14} /></div>
                        <span className="font-black text-lg tracking-tight">prepxai</span>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-muted" />
                </div>
                {/* Dummy Main Body Grid */}
                <div className="flex-1 p-6 grid grid-cols-12 gap-5 max-w-7xl w-full mx-auto">
                    <div className="col-span-3 border border-border bg-card rounded-2xl h-[300px]" />
                    <div className="col-span-6 space-y-4">
                        <div className="h-[120px] bg-slate-900 rounded-2xl" />
                        <div className="h-[250px] border border-border bg-card rounded-2xl" />
                    </div>
                    <div className="col-span-3 border border-border bg-card rounded-2xl h-[400px]" />
                </div>
            </div>

            {/* Dark background modal overlay */}
            <div className="absolute inset-0 bg-black/35 dark:bg-black/55 backdrop-blur-[2px] z-10" />

            {/* Popup Modal Box */}
            <div 
                className={cn(
                    "relative w-full border border-white/70 dark:border-[#1d1e26] bg-white dark:bg-[#111216] shadow-xl rounded-[14px] p-6 z-20 overflow-hidden transition-all duration-300",
                    "max-w-3xl min-h-[480px] flex flex-col justify-between"
                )}
            >
                {/* Top-Right Close Button */}
                {/* <button
                    type="button"
                    onClick={finishOnboarding}
                    className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-500 dark:hover:bg-white/5 dark:hover:text-[#ECEFF4] transition-colors"
                    aria-label="Close onboarding modal"
                >
                    <Icon name="X" size={14} />
                </button> */}

                {/* Progress bar at the top */}
                <div className="flex flex-col gap-1.5 mb-5 pr-8">
                    <div className="flex gap-1 h-[2.5px] w-full">
                        {Array.from({ length: totalSteps }).map((_, i) => (
                            <div
                                key={i}
                                className={cn(
                                    "h-full rounded-full flex-1 transition-all duration-300",
                                    step > i ? "bg-primary" : step === i ? "bg-primary/80" : "bg-muted"
                                )}
                            />
                        ))}
                    </div>
                    <span className="text-[9px] font-boldtracking-wider text-muted-foreground">
                        Step {step + 1} of {totalSteps}
                    </span>
                </div>

                {/* Animated Steps Container */}
                <div className="relative flex-1 flex flex-col justify-center">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={step}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                x: { type: "spring", stiffness: 120, damping: 20 },
                                opacity: { duration: 0.25 }
                            }}
                            className="w-full flex justify-center"
                        >
                            {renderStep()}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Fixed Navigation Bar */}
                {step > 0 && step < totalSteps - 1 && renderNavigation()}
            </div>
        </div>
    );
};

export default Onboarding;
